import { useMemo } from "react";
import { Box } from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import FormInput from "../Common/FormInputs/MuiTextField";
import MuiDatePicker from "../Common/FormInputs/MuiDatePicker";
import PlatformSelector from "./PlatformSelector";
import { getExpiryDateBounds } from "../../utils/inAppNotificationUtils";
import {
  IN_APP_NOTIFICATION_VALIDATIONS,
  pageFormMediumInputSx,
} from "../../constants/inAppNotificationConstant";

const DeliverySettingsRow = ({
  platform,
  onPlatformChange,
  platformError,
  expiryDate,
  onExpiryChange,
  expiryError,
  maxPerDay,
  onMaxPerDayChange,
  maxPerDayError,
  maxPerWeek,
  onMaxPerWeekChange,
  maxPerWeekError,
  readOnly = false,
}) => {
  const { minDate, maxDate } = useMemo(() => getExpiryDateBounds(), []);

  return (
    <Grid2
      container
      spacing={1}
      sx={{
        width: "100%",
        minWidth: 0,
        maxWidth: "100%",
        boxSizing: "border-box",
        alignItems: "flex-start",
        "& > .MuiGrid2-root": { minWidth: 0 },
      }}
    >
      <Grid2 size={{ xs: 12, sm: 6, lg: 3 }}>
        <PlatformSelector
          value={platform}
          onChange={onPlatformChange}
          error={platformError}
          readOnly={readOnly}
          compact={false}
          sx={pageFormMediumInputSx}
        />
      </Grid2>
      <Grid2 size={{ xs: 12, sm: 6, lg: 3 }}>
        <Box sx={{ minWidth: 0, overflow: "visible", maxWidth: { lg: 280 } }}>
          <MuiDatePicker
            label="Expiry Date"
            value={expiryDate}
            minDate={readOnly ? undefined : minDate}
            maxDate={readOnly ? undefined : maxDate}
            onChange={onExpiryChange}
            disabled={readOnly}
          />
          {expiryError ? (
            <Box
              component="span"
              sx={{ display: "block", color: "error.main", fontSize: "0.75rem", mt: 0.5 }}
            >
              {expiryError}
            </Box>
          ) : (
            !readOnly && (
              <Box
                component="span"
                sx={{ display: "block", color: "text.secondary", fontSize: "0.75rem", mt: 0.5 }}
              >
                {`Within ${IN_APP_NOTIFICATION_VALIDATIONS.expiryMaxDaysFromToday} days from today`}
              </Box>
            )
          )}
        </Box>
      </Grid2>
      <Grid2 size={{ xs: 12, sm: 6, lg: 3 }}>
        <FormInput
          id="maxPerDay"
          size="small"
          fullWidth
          type="number"
          label="Max per day"
          value={maxPerDay ?? ""}
          onChange={(e) => onMaxPerDayChange?.(e.target.value)}
          error={!!maxPerDayError}
          touched={maxPerDayError ? "true" : "false"}
          helperText={
            maxPerDayError ||
            `max ${IN_APP_NOTIFICATION_VALIDATIONS.configMaxPerDay.max}`
          }
          disabled={readOnly}
          sx={pageFormMediumInputSx}
          slotProps={{
            htmlInput: {
              min: IN_APP_NOTIFICATION_VALIDATIONS.configMaxPerDay.min,
              max: IN_APP_NOTIFICATION_VALIDATIONS.configMaxPerDay.max,
              step: 1,
            },
            formHelperText: {
              sx: {
                color: maxPerDayError ? "error.main" : "text.secondary",
                marginLeft: 0,
              },
            },
          }}
        />
      </Grid2>
      <Grid2 size={{ xs: 12, sm: 6, lg: 3 }}>
        <FormInput
          id="maxPerWeek"
          size="small"
          fullWidth
          type="number"
          label="Max per week"
          value={maxPerWeek ?? ""}
          onChange={(e) => onMaxPerWeekChange?.(e.target.value)}
          error={!!maxPerWeekError}
          touched={maxPerWeekError ? "true" : "false"}
          helperText={
            maxPerWeekError ||
            `max ${IN_APP_NOTIFICATION_VALIDATIONS.configMaxPerWeek.max}`
          }
          disabled={readOnly}
          sx={pageFormMediumInputSx}
          slotProps={{
            htmlInput: {
              min: IN_APP_NOTIFICATION_VALIDATIONS.configMaxPerWeek.min,
              max: IN_APP_NOTIFICATION_VALIDATIONS.configMaxPerWeek.max,
              step: 1,
            },
            formHelperText: {
              sx: {
                color: maxPerWeekError ? "error.main" : "text.secondary",
                marginLeft: 0,
              },
            },
          }}
        />
      </Grid2>
    </Grid2>
  );
};

export default DeliverySettingsRow;
